import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

const PAKKETTEN = [
  { value: 'trial',        label: 'Trial',        prijs: 0 },
  { value: 'forms_klein',  label: 'Forms Klein',  prijs: 29 },
  { value: 'forms_groot',  label: 'Forms Groot',  prijs: 79 },
  { value: 'starter',      label: 'Starter',      prijs: 199 },
  { value: 'professional', label: 'Professional', prijs: 399 },
  { value: 'business',     label: 'Business',     prijs: 699 },
  { value: 'enterprise',   label: 'Enterprise',   prijs: 0 },
];

function AdminAbonnementen() {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const [bedrijven, setBedrijven] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [filter, setFilter] = useState('alle');

  useEffect(() => {
    if (gebruiker?.rol !== 'super_admin' && gebruiker?.rol !== 'hoofdbeheerder') {
      navigate('/dashboard');
      return;
    }
    loadBedrijven();
  }, [gebruiker, navigate]);

  const loadBedrijven = async () => {
    try {
      const res = await api.get('/bedrijven');
      setBedrijven(res.data.bedrijven || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const updateBedrijf = async (id, changes) => {
    setSavingId(id);
    try {
      await api.patch(`/bedrijven/${id}/admin-licenties`, changes);
      setBedrijven(prev => prev.map(b => b.id === id ? { ...b, ...changes } : b));
    } catch (e) {
      alert('Wijzigen mislukt: ' + (e.response?.data?.message || e.message));
    } finally {
      setSavingId(null);
    }
  };

  const verlengTrial = async (bedrijf) => {
    const huidig = bedrijf.trial_eindigt_op ? new Date(bedrijf.trial_eindigt_op) : new Date();
    const nieuw = new Date(huidig);
    nieuw.setDate(nieuw.getDate() + 14);
    await updateBedrijf(bedrijf.id, { trial_eindigt_op: nieuw.toISOString() });
  };

  const filtered = bedrijven.filter(b => {
    if (filter === 'alle') return true;
    if (filter === 'forms') return b.forms_licentie && !b.werkorder_licentie;
    if (filter === 'werkorder') return b.werkorder_licentie;
    if (filter === 'trial') return b.abonnement === 'trial';
    if (filter === 'inactief') return !b.actief;
    return true;
  });

  const formatEuro = (n) => '€' + Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const formatDatum = (d) => d ? new Date(d).toLocaleDateString('nl-NL') : '—';

  if (loading) return <div style={{ padding: 32 }}>Laden...</div>;

  const card = { background: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const th = { padding: '12px 14px', textAlign: 'left', fontSize: 11, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, borderBottom: '2px solid #e2e8f0' };
  const td = { padding: '14px', borderBottom: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'middle' };

  const filterBtn = (val, label) => (
    <button
      key={val}
      onClick={() => setFilter(val)}
      style={{
        padding: '8px 14px',
        border: 'none',
        borderRadius: 6,
        cursor: 'pointer',
        background: filter === val ? '#4a9eff' : '#e2e8f0',
        color: filter === val ? 'white' : '#475569',
        fontWeight: 600,
        fontSize: 13,
      }}
    >{label}</button>
  );

  return (
    <>
      <h1 style={{ marginTop: 0, marginBottom: 8 }}>Abonnementen</h1>
      <p style={{ color: '#718096', marginTop: 0, marginBottom: 24 }}>Beheer pakketten, licenties en trial-periodes per bedrijf.</p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {filterBtn('alle', `Alle (${bedrijven.length})`)}
        {filterBtn('werkorder', 'Werkorder')}
        {filterBtn('forms', 'Forms-only')}
        {filterBtn('trial', 'In trial')}
        {filterBtn('inactief', 'Inactief')}
      </div>

      <div style={card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Bedrijf</th>
              <th style={th}>Pakket</th>
              <th style={th}>Werkorder</th>
              <th style={th}>Forms</th>
              <th style={th}>Trial t/m</th>
              <th style={th}>Actief</th>
              <th style={th}>Omzet/mnd</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(b => {
              const pakket = PAKKETTEN.find(p => p.value === b.abonnement);
              const omzet = pakket && b.actief && !['trial', 'enterprise'].includes(b.abonnement) ? pakket.prijs : 0;
              const isSaving = savingId === b.id;
              return (
                <tr key={b.id} style={{ opacity: isSaving ? 0.5 : 1 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{b.naam}</div>
                    <div style={{ fontSize: 12, color: '#718096' }}>{b.email}</div>
                  </td>
                  <td style={td}>
                    <select
                      value={b.abonnement || 'trial'}
                      onChange={e => updateBedrijf(b.id, { abonnement: e.target.value })}
                      disabled={isSaving}
                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e0', fontSize: 13 }}
                    >
                      {PAKKETTEN.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </td>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={!!b.werkorder_licentie}
                      onChange={e => updateBedrijf(b.id, { werkorder_licentie: e.target.checked })}
                      disabled={isSaving}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={!!b.forms_licentie}
                      onChange={e => updateBedrijf(b.id, { forms_licentie: e.target.checked })}
                      disabled={isSaving}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="date"
                      value={b.trial_eindigt_op ? new Date(b.trial_eindigt_op).toISOString().split('T')[0] : ''}
                      onChange={e => {
                        const v = e.target.value;
                        if (!v) return;
                        updateBedrijf(b.id, { trial_eindigt_op: new Date(v + 'T23:59:59').toISOString() });
                      }}
                      disabled={isSaving}
                      style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #cbd5e0', fontSize: 13, fontFamily: 'inherit' }}
                    />
                  </td>
                  <td style={td}>
                    <input
                      type="checkbox"
                      checked={!!b.actief}
                      onChange={e => updateBedrijf(b.id, { actief: e.target.checked })}
                      disabled={isSaving}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ ...td, fontWeight: 600, color: omzet > 0 ? '#48bb78' : '#a0aec0' }}>
                    {omzet > 0 ? formatEuro(omzet) : (b.abonnement === 'enterprise' ? 'op aanvraag' : '—')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 16, color: '#718096', fontSize: 13 }}>
        Wijzigingen worden direct opgeslagen. Forms-only zet automatisch werkorder_licentie uit als je het pakket selecteert? <strong>Nog niet</strong> — je moet handmatig de checkboxes kloppend zetten.
      </p>
    </>
  );
}

export default AdminAbonnementen;
